import { reconcileEvaluationSheetImagesAfterSave } from '../utils/evaluationSheetImageLifecycle';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AutoStatusToast from '../components/common/AutoStatusToast';
import BasicSettingIcon from '../components/common/BasicSettingIcon';
import ListIcon from '../components/common/ListIcon';
import EvaluationStandardListPanel from '../components/evaluationPlan/EvaluationStandardListPanel';
import EvaluationPlanListPanel from '../components/evaluationPlan/EvaluationPlanListPanel';
import EvaluationPlanAppliedListPanel from '../components/evaluationPlan/EvaluationPlanAppliedListPanel';
import EvaluationPlanFormModal from '../components/evaluationPlan/EvaluationPlanFormModal';
import EvaluationPlanDocumentListModal from '../components/evaluationPlan/EvaluationPlanDocumentListModal';
import EvaluationStandardFormModal from '../components/evaluationPlan/EvaluationStandardFormModal';
import EvaluationStandardBulkEditModal from '../components/evaluationPlan/EvaluationStandardBulkEditModal';
import EvaluationPlanWorkspaceLevelSettingsModal from '../components/evaluationPlan/EvaluationPlanWorkspaceLevelSettingsModal';
import ProgramToolbarUndoRedoButtons from '../components/program/ProgramToolbarUndoRedoButtons';
import { useAutoStatusToast } from '../hooks/useAutoStatusToast';
import { GradeTrackLeaveContext } from '../contexts/GradeTrackLeaveContext';
import { useProgramSidebar } from '../contexts/ProgramSidebarContext';
import { parseGradeDisplayName } from '../utils/gradeDisplayName';
import { getAdminEvaluationPlanCategories } from '../services/adminEvaluationPlanService';
import {
  clearWorkspaceEvaluationPlanSlotAttachment,
  createWorkspaceEvaluationPlanCategory,
  deleteWorkspaceEvaluationPlanCategory,
  getWorkspaceEvaluationPlanCategories,
  getWorkspaceEvaluationPlanCategoryBundle,
  renameWorkspaceEvaluationPlanCategory,
  reorderWorkspaceEvaluationPlanCategories,
  saveWorkspaceEvaluationPlanCategoryBundle,
  saveWorkspaceEvaluationPlanUserPlansOnly,
  updateWorkspaceEvaluationPlanCategoryLevelStep,
} from '../services/workspaceEvaluationPlanService';
import {
  getWorkspaceEvaluationPlanLevelSettings,
  saveWorkspaceEvaluationPlanLevelSettings,
} from '../services/workspaceEvaluationPlanLevelSettingsService';
import { getTeamLeaderWorkspaceIdForLearningShare } from '../services/teamService';
import { getWorkspaceShareGradeId } from '../utils/workspaceShareGradeId';
import {
  replaceWorkspaceEvaluationPlanApplied,
  type WorkspaceEvaluationPlanAppliedSnapshot,
} from '../services/workspaceEvaluationPlanAppliedService';
import {
  loadWorkspaceEvaluationPlanAppliedEntries,
  resolveWorkspaceEvaluationPlanApplied,
  saveWorkspaceEvaluationPlanAppliedEntries,
  saveWorkspaceEvaluationPlanAppliedSnapshots,
  type WorkspaceEvaluationPlanAppliedEntry,
} from '../utils/workspaceEvaluationPlanAppliedCache';
import type {
  EvaluationLevelStepCount,
  EvaluationPlanAttachmentSlot,
  EvaluationPlanDraft,
  EvaluationPlanRowScope,
  EvaluationPlanWorkspaceDraft,
  EvaluationStandardDraft,
  WorkspaceEvaluationPlanLevelSettings,
} from '../types/evaluationPlan';
import {
  DEFAULT_LEVEL_STEP_COUNT,
  LEVEL_STEP_COUNT_OPTIONS,
  categoryLevelMetaKey,
  clampLevelStepCount,
  cloneEvaluationPlanDraft,
  createEmptyStandardDraft,
  emptyWorkspaceEvaluationPlanLevelSettings,
  formatLevelStepOptionLabel,
  getLevelDisplayLabelsForStep,
  normalizeEvaluationPlanWorkspaceDraft,
  parseEvaluationPlanAttachment,
  planLinksStandard,
} from '../types/evaluationPlan';
import {
  EMPTY_EVALUATION_PLAN_CATEGORY_SELECTION,
  type EvaluationPlanCategoryScope,
  type EvaluationPlanCategorySelection,
  resolveEvaluationPlanCategorySelection,
} from '../utils/evaluationPlanCategorySelection';
import {
  getEditablePlanIds,
  getEditableStandardIds,
  loadMergedEvaluationPlanCategoryView,
} from '../utils/evaluationPlanCategoryBundleMerge';
import { buildEvaluationPlanDocumentListItems, type EvaluationPlanDocumentListItem } from '../utils/evaluationPlanDocumentList';
import { loadTeamLeaderWorkspaceIdForLearningShareFromCache } from '../utils/myTeamMembershipCache';
import {
  loadWorkspaceEvaluationPlanSessionState,
  saveWorkspaceEvaluationPlanSessionEntry,
  type WorkspaceEvaluationPlanSessionEntry,
} from '../utils/workspaceEvaluationPlanCache';

const SortableUserCategoryItem: React.FC<{
  id: string;
  active: boolean;
  busy: boolean;
  onSelect: () => void;
  onRename: () => void;
  onDelete: () => void;
}> = ({ id, active, busy, onSelect, onRename, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.84 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`cp-admin-learning-template-item cp-evaluation-plan-category-item${active ? ' is-active' : ''}${isDragging ? ' is-dragging' : ''}`}
      aria-selected={active}
      aria-label={`${id}, 사용자 카테고리`}
      onClick={onSelect}
      {...attributes}
      {...listeners}
    >
      <div className="cp-admin-learning-template-tab cp-admin-learning-template-tab--with-dot">
        <span className="cp-admin-learning-template-dot cp-admin-learning-template-dot--user" aria-hidden />
        <span className="cp-admin-learning-template-tab-label">{id}</span>
      </div>
      <div className="cp-admin-learning-template-item-actions">
        <button type="button" className="track-icon-button cp-admin-learning-template-icon-btn" title="카테고리 이름 수정" onClick={(e) => { e.stopPropagation(); onRename(); }} disabled={busy} aria-label="카테고리 수정">
          <img src="/edit.svg" alt="" className="track-icon" />
        </button>
        <button type="button" className="track-icon-button cp-admin-learning-template-icon-btn cp-admin-learning-template-icon-btn-delete" title="카테고리 삭제" onClick={(e) => { e.stopPropagation(); onDelete(); }} disabled={busy} aria-label="카테고리 삭제">
          <img src="/Trash.svg" alt="" className="track-icon" />
        </button>
      </div>
    </div>
  );
};

const ReadonlyAdminCategoryTab: React.FC<{ name: string; active: boolean; busy: boolean; onSelect: () => void }> = ({
  name, active, busy, onSelect,
}) => (
  <div
    role="tab"
    className={`cp-admin-learning-template-item cp-admin-learning-template-item--readonly cp-evaluation-plan-category-item${active ? ' is-active' : ''}`}
    aria-selected={active}
    title="관리자「평가계획 관리」카테고리(읽기 전용)"
    onClick={() => { if (!busy) onSelect(); }}
    tabIndex={0}
  >
    <div className="cp-admin-learning-template-tab cp-admin-learning-template-tab--with-dot">
      <span className="cp-admin-learning-template-dot cp-admin-learning-template-dot--default" aria-hidden />
      <span className="cp-admin-learning-template-tab-label">{name}</span>
    </div>
  </div>
);

const ReadonlyTeamCategoryTab: React.FC<{ name: string; active: boolean; busy: boolean; onSelect: () => void }> = ({
  name, active, busy, onSelect,
}) => (
  <div
    role="tab"
    className={`cp-admin-learning-template-item cp-admin-learning-template-item--readonly cp-evaluation-plan-category-item${active ? ' is-active' : ''}`}
    aria-selected={active}
    title="팀장 카테고리(읽기 전용)"
    onClick={() => { if (!busy) onSelect(); }}
    tabIndex={0}
  >
    <div className="cp-admin-learning-template-tab cp-admin-learning-template-tab--with-dot">
      <span className="cp-admin-learning-template-dot cp-admin-learning-template-dot--team" aria-hidden />
      <span className="cp-admin-learning-template-tab-label">{name}</span>
    </div>
  </div>
);

function emptyDraft(categoryName: string): EvaluationPlanWorkspaceDraft {
  return { categoryName, standards: [], plans: [], deletedStandardIds: [], deletedPlanIds: [] };
}

function userEditableSubsetDraft(
  draft: EvaluationPlanWorkspaceDraft,
  planScopeById: Record<string, EvaluationPlanRowScope>,
  standardScopeById: Record<string, EvaluationPlanRowScope>
): EvaluationPlanWorkspaceDraft {
  return {
    categoryName: draft.categoryName,
    standards: draft.standards.filter((s) => standardScopeById[s.id] === 'user'),
    plans: draft.plans.filter((p) => planScopeById[p.id] === 'user'),
    deletedStandardIds: draft.deletedStandardIds,
    deletedPlanIds: draft.deletedPlanIds,
  };
}

function normalizePlanScope(raw: unknown, fallback: EvaluationPlanRowScope = 'user'): EvaluationPlanRowScope {
  return raw === 'admin' || raw === 'team' || raw === 'user' ? raw : fallback;
}

/** 적용본 vs 원본 본문·첨부 비교(id 제외). 출처 색 복구·사용자 유형 전환용. */
function evaluationPlanTextEqual(a: EvaluationPlanDraft, b: EvaluationPlanDraft): boolean {
  const pack = (plan: EvaluationPlanDraft) =>
    JSON.stringify({
      evaluation_element_text: (plan.evaluation_element_text ?? '').trim(),
      evaluation_method_text: (plan.evaluation_method_text ?? '').trim(),
      unit_text: (plan.unit_text ?? '').trim(),
      lesson_evaluation_text: (plan.lesson_evaluation_text ?? '').trim(),
      level_a: (plan.level_a ?? '').trim(),
      level_b: (plan.level_b ?? '').trim(),
      level_c: (plan.level_c ?? '').trim(),
      level_d: (plan.level_d ?? '').trim(),
      level_e: (plan.level_e ?? '').trim(),
      standard_ids: [...(plan.standard_ids ?? [])].map((id) => id.trim()).filter(Boolean).sort(),
      criterion_rubric_attachment: parseEvaluationPlanAttachment(plan.criterion_rubric_attachment),
      evaluation_sheet_attachment: parseEvaluationPlanAttachment(plan.evaluation_sheet_attachment),
    });
  return pack(a) === pack(b);
}

/**
 * 목록/라이브 카탈로그 기준 사용자 소유 여부.
 * 적용본 표시 스코프(appliedPlanScopeById)는 수정 중 임시로 user가 될 수 있어 여기 쓰지 않는다.
 */
function isCatalogUserOwnedPlan(params: {
  planId: string;
  liveScope?: EvaluationPlanRowScope;
  planScopeById: Record<string, EvaluationPlanRowScope>;
}): boolean {
  const id = params.planId.trim();
  if (!id) return false;
  if (params.liveScope === 'user') return true;
  return params.planScopeById[id] === 'user';
}

function resolveAppliedPlanScope(params: {
  entryScope?: EvaluationPlanRowScope;
  displayScope?: EvaluationPlanRowScope;
  liveScope?: EvaluationPlanRowScope;
  snapScope?: EvaluationPlanRowScope;
  categoryScope?: EvaluationPlanRowScope;
}): EvaluationPlanRowScope {
  return normalizePlanScope(
    params.displayScope ??
      params.entryScope ??
      params.liveScope ??
      params.snapScope ??
      params.categoryScope,
    'user'
  );
}

/** 기본/팀장 적용본을 수정해 사용자 출처가 되면 목록용 사용자 평가계획으로 분기. */
function forkAppliedPlanIntoUserList(params: {
  sourcePlanId: string;
  editedPlan: EvaluationPlanDraft;
  categoryName: string;
  draftPlansLength: number;
}): EvaluationPlanDraft {
  const categoryName = params.categoryName.trim();
  return {
    ...cloneEvaluationPlanDraft(params.editedPlan),
    id: crypto.randomUUID(),
    category_name: categoryName || params.editedPlan.category_name || '',
    sort_order: params.draftPlansLength,
  };
}

function setAppliedPlanDisplayScope(
  planId: string,
  scope: EvaluationPlanRowScope,
  setAppliedEntries: React.Dispatch<React.SetStateAction<WorkspaceEvaluationPlanAppliedEntry[]>>,
  setAppliedPlanScopeById: React.Dispatch<React.SetStateAction<Record<string, EvaluationPlanRowScope>>>,
  setSavedAppliedSnapshots: React.Dispatch<React.SetStateAction<WorkspaceEvaluationPlanAppliedSnapshot[]>>,
  persistAppliedEntries: (next: WorkspaceEvaluationPlanAppliedEntry[]) => void
) {
  const id = planId.trim();
  if (!id) return;
  setAppliedPlanScopeById((prev) => (prev[id] === scope ? prev : { ...prev, [id]: scope }));
  setAppliedEntries((prev) => {
    let changed = false;
    const next = prev.map((entry) => {
      if (entry.planId !== id || entry.planScope === scope) return entry;
      changed = true;
      return { ...entry, planScope: scope };
    });
    if (changed) persistAppliedEntries(next);
    return changed ? next : prev;
  });
  setSavedAppliedSnapshots((prev) =>
    prev.map((snap) =>
      snap.source_plan_id === id && snap.source_plan_scope !== scope
        ? { ...snap, source_plan_scope: scope }
        : snap
    )
  );
}

function readWorkspaceEvaluationPlanSessionEntry(
  workspaceId: string,
  gradeId: string,
  categoryName: string
): WorkspaceEvaluationPlanSessionEntry | null {
  const name = categoryName.trim();
  const gid = gradeId.trim();
  if (!name || !gid) return null;
  const session = loadWorkspaceEvaluationPlanSessionState(workspaceId, gid)?.draftsByCategory[name];
  if (!session || session.draft.categoryName !== name) return null;
  return session;
}

function cloneWorkspaceEvaluationPlanSessionDraft(
  entry: WorkspaceEvaluationPlanSessionEntry
): EvaluationPlanWorkspaceDraft {
  return JSON.parse(JSON.stringify(entry.draft)) as EvaluationPlanWorkspaceDraft;
}

const WorkspaceEvaluationPlanPage: React.FC<{ isActive?: boolean }> = ({ isActive = true }) => {
  const { workspaceId, programFocusGradeId, grades } = useProgramSidebar();
  const focusGradeId = (programFocusGradeId ?? '').trim();
  const focusClassDisplay = React.useMemo(() => {
    const grade = grades.find((g) => g.id === focusGradeId);
    if (!grade) return { label: '', gradeNumbers: [] as number[] };
    const parsed = parseGradeDisplayName(grade.name);
    return { label: parsed.title.trim(), gradeNumbers: parsed.grades };
  }, [focusGradeId, grades]);
  /** AI 평가 구성: 현재 학급 학년의 학습내용만 검색 (내 학습내용 우선 → 없으면 관리자 학습내용 같은 학년) */
  const aiClassScope = React.useMemo(
    () => (focusGradeId ? { gradeId: focusGradeId, gradeNumbers: focusClassDisplay.gradeNumbers } : null),
    [focusGradeId, focusClassDisplay.gradeNumbers]
  );
  const { setWorkspaceEvaluationPlanDirty, saveWorkspaceEvaluationPlanRef } = React.useContext(GradeTrackLeaveContext);
  const { toastState, showSuccess } = useAutoStatusToast(4000);

  const [adminCategoryNames, setAdminCategoryNames] = useState<string[]>([]);
  const [teamCategoryNames, setTeamCategoryNames] = useState<string[]>([]);
  const [userCategoryNames, setUserCategoryNames] = useState<string[]>([]);
  const [teamWorkspaceId, setTeamWorkspaceId] = useState<string | null>(null);
  const [teamShareGradeId, setTeamShareGradeId] = useState<string | null>(null);
  const [categorySelection, setCategorySelection] = useState<EvaluationPlanCategorySelection>(
    EMPTY_EVALUATION_PLAN_CATEGORY_SELECTION
  );
  const [categoryLevelStepByKey, setCategoryLevelStepByKey] = useState<Record<string, EvaluationLevelStepCount>>({});
  const [workspaceLevelSettings, setWorkspaceLevelSettings] = useState<WorkspaceEvaluationPlanLevelSettings>(
    () => emptyWorkspaceEvaluationPlanLevelSettings()
  );
  const [levelSettingsModalOpen, setLevelSettingsModalOpen] = useState(false);
  const [levelSettingsSaving, setLevelSettingsSaving] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoryBusy, setCategoryBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [draft, setDraft] = useState<EvaluationPlanWorkspaceDraft>(() => emptyDraft(''));
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const [baselineJson, setBaselineJson] = useState(() => normalizeEvaluationPlanWorkspaceDraft(emptyDraft('')));
  /** 저장 직전의 저장 상태 — 저장 후 빠진 평가지 이미지 정리용 */
  const baselineJsonForImagesRef = useRef(baselineJson);
  baselineJsonForImagesRef.current = baselineJson;
  const [planScopeById, setPlanScopeById] = useState<Record<string, EvaluationPlanRowScope>>({});
  const [standardScopeById, setStandardScopeById] = useState<Record<string, EvaluationPlanRowScope>>({});
  const planScopeByIdRef = useRef(planScopeById);
  const standardScopeByIdRef = useRef(standardScopeById);
  planScopeByIdRef.current = planScopeById;
  standardScopeByIdRef.current = standardScopeById;

  const [selectedStandardId, setSelectedStandardId] = useState<string | null>(null);
  const [filterQuery, setFilterQuery] = useState('');
  const [planFilterQuery, setPlanFilterQuery] = useState('');
  const [appliedEntries, setAppliedEntries] = useState<WorkspaceEvaluationPlanAppliedEntry[]>([]);
  const [selectedPlanIds, setSelectedPlanIds] = useState<string[]>([]);
  const [savedAppliedSnapshots, setSavedAppliedSnapshots] = useState<WorkspaceEvaluationPlanAppliedSnapshot[]>([]);
  const [appliedPlans, setAppliedPlans] = useState<EvaluationPlanDraft[]>([]);
  const [appliedStandards, setAppliedStandards] = useState<EvaluationStandardDraft[]>([]);
  const [appliedPlanScopeById, setAppliedPlanScopeById] = useState<Record<string, EvaluationPlanRowScope>>({});
  const [appliedCategoryByPlanId, setAppliedCategoryByPlanId] = useState<Record<string, string>>({});
  const appliedEntriesRef = useRef(appliedEntries);
  const appliedPlansRef = useRef(appliedPlans);
  const appliedStandardsRef = useRef(appliedStandards);
  const appliedPlanScopeByIdRef = useRef(appliedPlanScopeById);
  const savedAppliedSnapshotsRef = useRef(savedAppliedSnapshots);
  appliedEntriesRef.current = appliedEntries;
  appliedPlansRef.current = appliedPlans;
  appliedStandardsRef.current = appliedStandards;
  appliedPlanScopeByIdRef.current = appliedPlanScopeById;
  savedAppliedSnapshotsRef.current = savedAppliedSnapshots;
  const liveAppliedBySourceRef = useRef<
    Map<
      string,
      {
        plan: EvaluationPlanDraft;
        standards: EvaluationStandardDraft[];
        scope: EvaluationPlanRowScope;
        categoryName: string;
      }
    >
  >(new Map());

  const [standardModalOpen, setStandardModalOpen] = useState(false);
  const [standardModalMode, setStandardModalMode] = useState<'create' | 'edit'>('create');
  const [standardModalInitial, setStandardModalInitial] = useState<EvaluationStandardDraft | null>(null);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [planModalMode, setPlanModalMode] = useState<'create' | 'edit'>('create');
  const [planModalInitial, setPlanModalInitial] = useState<EvaluationPlanDraft | null>(null);
  const [planModalReadOnly, setPlanModalReadOnly] = useState(false);
  const [planModalTarget, setPlanModalTarget] = useState<'draft' | 'applied'>('draft');

  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryLevelStep, setNewCategoryLevelStep] = useState<EvaluationLevelStepCount>(DEFAULT_LEVEL_STEP_COUNT);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renameSourceName, setRenameSourceName] = useState('');
  const [renameDraftName, setRenameDraftName] = useState('');
  const [renameLevelStep, setRenameLevelStep] = useState<EvaluationLevelStepCount>(DEFAULT_LEVEL_STEP_COUNT);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetName, setDeleteTargetName] = useState('');

  const [criterionDocListOpen, setCriterionDocListOpen] = useState(false);
  const [sheetDocListOpen, setSheetDocListOpen] = useState(false);
  const [criterionDocListItems, setCriterionDocListItems] = useState<EvaluationPlanDocumentListItem[]>([]);
  const [sheetDocListItems, setSheetDocListItems] = useState<EvaluationPlanDocumentListItem[]>([]);
  const [criterionDocListLoading, setCriterionDocListLoading] = useState(false);
  const [sheetDocListLoading, setSheetDocListLoading] = useState(false);
  const [criterionDocListError, setCriterionDocListError] = useState<string | null>(null);
  const [sheetDocListError, setSheetDocListError] = useState<string | null>(null);

  const categoryScope = categorySelection.scope;
  const categoryName = categorySelection.name;
  const isUserCategory = categoryScope === 'user';
  const editablePlanIds = useMemo(() => getEditablePlanIds(planScopeById), [planScopeById]);
  const editableStandardIds = useMemo(() => getEditableStandardIds(standardScopeById), [standardScopeById]);

  const currentCategoryMetaKey = categoryLevelMetaKey(categoryScope, categoryName);
  const currentLevelStepCount =
    categoryLevelStepByKey[currentCategoryMetaKey] ??
    workspaceLevelSettings.defaultLevelStepCount ??
    DEFAULT_LEVEL_STEP_COUNT;
  const currentLevelDisplayLabels = useMemo(
    () => getLevelDisplayLabelsForStep(workspaceLevelSettings.labelsByStep, currentLevelStepCount),
    [workspaceLevelSettings.labelsByStep, currentLevelStepCount]
  );

  const isDirty = useMemo(() => {
    const savedAppliedIds = savedAppliedSnapshots
      .map((snap) => (snap.source_plan_id ?? '').trim())
      .filter(Boolean)
      .sort()
      .join('|');
    const currentAppliedIds = appliedEntries
      .map((entry) => entry.planId.trim())
      .filter(Boolean)
      .sort()
      .join('|');
    const appliedMembershipDirty = savedAppliedIds !== currentAppliedIds;
    const appliedBodyDirty =
      !appliedMembershipDirty &&
      appliedEntries.some((entry) => {
        const planId = entry.planId.trim();
        if (!planId) return false;
        const snap = savedAppliedSnapshots.find((row) => (row.source_plan_id ?? '').trim() === planId);
        if (!snap) return true;
        const plan = appliedPlans.find((row) => row.id === planId);
        if (!plan) return false;
        if (!evaluationPlanTextEqual(plan, { ...snap.plan, id: planId })) return true;
        const savedScope = normalizePlanScope(snap.source_plan_scope, snap.source_category_scope);
        const currentScope = normalizePlanScope(entry.planScope, snap.source_category_scope);
        return savedScope !== currentScope;
      });
    const appliedDirty = appliedMembershipDirty || appliedBodyDirty;
    if (isUserCategory) {
      return normalizeEvaluationPlanWorkspaceDraft(draft) !== baselineJson || appliedDirty;
    }
    return (
      normalizeEvaluationPlanWorkspaceDraft(
        userEditableSubsetDraft(draft, planScopeById, standardScopeById)
      ) !== baselineJson || appliedDirty
    );
  }, [
    draft,
    baselineJson,
    isUserCategory,
    planScopeById,
    standardScopeById,
    savedAppliedSnapshots,
    appliedEntries,
    appliedPlans,
  ]);

  const visiblePlans = useMemo(() => {
    if (!selectedStandardId) return draft.plans;
    return draft.plans.filter((plan) => planLinksStandard(plan, selectedStandardId));
  }, [draft.plans, selectedStandardId]);

  const appliedPlanIds = useMemo(
    () => new Set(appliedEntries.map((entry) => entry.planId)),
    [appliedEntries]
  );

  const selectedPlanIdSet = useMemo(() => new Set(selectedPlanIds), [selectedPlanIds]);

  const pendingApplyCount = useMemo(
    () => selectedPlanIds.filter((id) => !appliedPlanIds.has(id)).length,
    [selectedPlanIds, appliedPlanIds]
  );

  const persistAppliedEntries = useCallback(
    (next: WorkspaceEvaluationPlanAppliedEntry[]) => {
      if (!workspaceId || !focusGradeId) return;
      saveWorkspaceEvaluationPlanAppliedEntries(workspaceId, focusGradeId, next);
    },
    [workspaceId, focusGradeId]
  );

  const toggleSelectPlan = useCallback(
    (planId: string) => {
      const id = planId.trim();
      if (!id) return;
      if (appliedEntriesRef.current.some((entry) => entry.planId === id)) return;
      setSelectedPlanIds((prev) => (prev.includes(id) ? prev.filter((row) => row !== id) : [...prev, id]));
    },
    []
  );

  const applySelectedPlans = useCallback(() => {
    const cat = categoryName.trim();
    if (!cat) return;
    const pending = selectedPlanIds.filter((id) => id.trim() && !appliedPlanIds.has(id));
    if (!pending.length) return;
    setAppliedEntries((prev) => {
      const existing = new Set(prev.map((entry) => entry.planId));
      const additions: WorkspaceEvaluationPlanAppliedEntry[] = [];
      for (const planId of pending) {
        if (existing.has(planId)) continue;
        additions.push({
          planId,
          categoryName: cat,
          categoryScope,
          planScope: normalizePlanScope(planScopeById[planId], isUserCategory ? 'user' : categoryScope),
        });
      }
      if (!additions.length) return prev;
      const next = [...prev, ...additions];
      persistAppliedEntries(next);
      return next;
    });
    setSelectedPlanIds((prev) => prev.filter((id) => !pending.includes(id) && !appliedPlanIds.has(id)));
  }, [
    categoryName,
    categoryScope,
    isUserCategory,
    planScopeById,
    selectedPlanIds,
    appliedPlanIds,
    persistAppliedEntries,
  ]);

  const removeAppliedPlan = useCallback(
    (planId: string) => {
      const id = planId.trim();
      if (!id) return;
      setAppliedEntries((prev) => {
        if (!prev.some((entry) => entry.planId === id)) return prev;
        const next = prev.filter((entry) => entry.planId !== id);
        persistAppliedEntries(next);
        return next;
      });
      setSelectedPlanIds((prev) => prev.filter((row) => row !== id));
    },
    [persistAppliedEntries]
  );

  const syncUserPlanAcrossListAndApplied = useCallback((plan: EvaluationPlanDraft) => {
    const id = plan.id.trim();
    if (!id) return;
    setDraft((prev) => {
      if (!prev.plans.some((row) => row.id === id)) return prev;
      const next = { ...prev, plans: prev.plans.map((row) => (row.id === id ? plan : row)) };
      draftRef.current = next;
      return next;
    });
    setAppliedPlans((prev) => {
      if (!prev.some((row) => row.id === id)) return prev;
      const next = prev.map((row) => (row.id === id ? plan : row));
      appliedPlansRef.current = next;
      return next;
    });
    const live = liveAppliedBySourceRef.current.get(id);
    if (live) {
      liveAppliedBySourceRef.current.set(id, { ...live, plan, scope: 'user' });
    }
    setSavedAppliedSnapshots((prev) => {
      const next = prev.map((snap) =>
        snap.source_plan_id === id
          ? { ...snap, plan: { ...plan, id: snap.id }, source_plan_scope: 'user' as const }
          : snap
      );
      savedAppliedSnapshotsRef.current = next;
      return next;
    });
  }, []);

  const commitDraft = useCallback((updater: (prev: EvaluationPlanWorkspaceDraft) => EvaluationPlanWorkspaceDraft) => {
    const next = updater(draftRef.current);
    draftRef.current = next;
    setDraft(next);
    return next;
  }, []);

  const bundleReady = !categoryName || draft.categoryName === categoryName;

  const categoryDndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const loadCategories = useCallback(async () => {
    if (!workspaceId || !focusGradeId) {
      setCategoriesLoading(false);
      setAdminCategoryNames([]);
      setTeamCategoryNames([]);
      setUserCategoryNames([]);
      setTeamWorkspaceId(null);
      setTeamShareGradeId(null);
      return;
    }
    setCategoriesLoading(true);
    setError(null);
    try {
      const teamWsCached = loadTeamLeaderWorkspaceIdForLearningShareFromCache(workspaceId);
      const teamWs =
        teamWsCached === undefined
          ? await getTeamLeaderWorkspaceIdForLearningShare(workspaceId)
          : teamWsCached;
      setTeamWorkspaceId(teamWs);
      const teamShare = teamWs ? await getWorkspaceShareGradeId(teamWs).catch(() => null) : null;
      setTeamShareGradeId(teamShare);

      const [adminRows, userRows, teamRows] = await Promise.all([
        getAdminEvaluationPlanCategories(),
        getWorkspaceEvaluationPlanCategories(workspaceId, focusGradeId),
        teamWs && teamShare ? getWorkspaceEvaluationPlanCategories(teamWs, teamShare) : Promise.resolve([]),
      ]);

      const adminNames = adminRows.map((r) => r.category_name.trim()).filter(Boolean);
      const personalNames = userRows.map((r) => r.category_name.trim()).filter(Boolean);
      const teamNames = teamRows.map((r) => r.category_name.trim()).filter(Boolean);
      const levelSteps: Record<string, EvaluationLevelStepCount> = {};
      const ingestRows = (rows: Awaited<ReturnType<typeof getAdminEvaluationPlanCategories>>, scope: EvaluationPlanRowScope) => {
        rows.forEach((row) => {
          const n = row.category_name.trim();
          if (!n) return;
          levelSteps[categoryLevelMetaKey(scope, n)] = clampLevelStepCount(row.level_step_count);
        });
      };
      ingestRows(adminRows, 'admin');
      ingestRows(userRows, 'user');
      if (teamWs && teamWs !== workspaceId) ingestRows(teamRows, 'team');

      setAdminCategoryNames(adminNames);
      setUserCategoryNames(personalNames);
      setTeamCategoryNames(teamWs && teamWs !== workspaceId ? teamNames : []);
      setCategoryLevelStepByKey(levelSteps);

      let nextSelection = EMPTY_EVALUATION_PLAN_CATEGORY_SELECTION;
      setCategorySelection((prev) => {
        nextSelection = resolveEvaluationPlanCategorySelection(
          prev,
          adminNames,
          personalNames,
          teamWs && teamWs !== workspaceId ? teamNames : []
        );
        return nextSelection;
      });

      const sessionEntry =
        nextSelection.name ? readWorkspaceEvaluationPlanSessionEntry(workspaceId, focusGradeId, nextSelection.name) : null;
      if (sessionEntry) {
        const cloned = cloneWorkspaceEvaluationPlanSessionDraft(sessionEntry);
        setDraft(cloned);
        setBaselineJson(sessionEntry.baselineJson);
        setPlanScopeById(sessionEntry.planScopeById ?? {});
        setStandardScopeById(sessionEntry.standardScopeById ?? {});
        setSelectedStandardId((prev) =>
          prev && cloned.standards.some((row) => row.id === prev) ? prev : null
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : '카테고리를 불러오지 못했습니다.');
    } finally {
      setCategoriesLoading(false);
    }
  }, [workspaceId, focusGradeId]);

  const loadLevelSettings = useCallback(async () => {
    if (!workspaceId || !focusGradeId) {
      setWorkspaceLevelSettings(emptyWorkspaceEvaluationPlanLevelSettings());
      return;
    }
    try {
      const settings = await getWorkspaceEvaluationPlanLevelSettings(workspaceId, focusGradeId);
      setWorkspaceLevelSettings(settings);
    } catch (e) {
      setError(e instanceof Error ? e.message : '성취수준 표시 설정을 불러오지 못했습니다.');
    }
  }, [workspaceId, focusGradeId]);

  const loadBundle = useCallback(async () => {
    const name = categoryName.trim();
    if (!workspaceId || !focusGradeId || !name) {
      const empty = emptyDraft('');
      setDraft(empty);
      setBaselineJson(normalizeEvaluationPlanWorkspaceDraft(empty));
      setPlanScopeById({});
      setStandardScopeById({});
      return;
    }
    setError(null);
    try {
      const merged = await loadMergedEvaluationPlanCategoryView({
        categoryScope,
        categoryName: name,
        workspaceId,
        gradeId: focusGradeId,
        teamWorkspaceId: teamWorkspaceId,
        teamShareGradeId: teamShareGradeId,
      });
      const next: EvaluationPlanWorkspaceDraft = {
        categoryName: name,
        standards: merged.standards,
        plans: merged.plans,
        deletedStandardIds: [],
        deletedPlanIds: [],
      };
      setDraft(next);
      setPlanScopeById(merged.planScopeById);
      setStandardScopeById(merged.standardScopeById);
      const baseline = isUserCategory
        ? normalizeEvaluationPlanWorkspaceDraft(next)
        : normalizeEvaluationPlanWorkspaceDraft(
            userEditableSubsetDraft(next, merged.planScopeById, merged.standardScopeById)
          );
      setBaselineJson(baseline);
      saveWorkspaceEvaluationPlanSessionEntry(workspaceId, focusGradeId, name, {
        draft: next,
        baselineJson: baseline,
        planScopeById: merged.planScopeById,
        standardScopeById: merged.standardScopeById,
      });
      setSelectedStandardId((prev) =>
        prev && merged.standards.some((row) => row.id === prev) ? prev : null
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : '데이터를 불러오지 못했습니다.');
    }
  }, [categoryScope, categoryName, workspaceId, focusGradeId, teamWorkspaceId, teamShareGradeId, isUserCategory]);

  /**
   * 관리자·팀 카테고리: 탭 세션 초안(sessionStorage)을 먼저 즉시 복원한 뒤, 버전 검증된 관리자·팀 행으로 교체한다.
   * - 관리자 번들은 IndexedDB 캐시 + 버전(행 수·max(updated_at)) head 확인 → 같으면 행 다운로드 없음, 다르면 다시 받음
   *   (getAdminEvaluationPlanCategoryBundle / 팀은 기존 LS 버전 캐시)
   * - 이전 버그: 세션 초안이 검증 없이 우선돼, 관리자가 나중에 등록한 성취기준 대신 처음 본 빈 목록이 계속 보임
   * - 사용자 범위(user) 행·삭제 표시는 세션 초안 것을 유지 → 미저장 사용자 편집 보존.
   */
  const refreshSharedRowsFromServer = useCallback(
    async (scope: typeof categoryScope, name: string, sessionEntry: WorkspaceEvaluationPlanSessionEntry) => {
      if (scope === 'user') return;
      try {
        const merged = await loadMergedEvaluationPlanCategoryView({
          categoryScope: scope,
          categoryName: name,
          workspaceId,
          gradeId: focusGradeId,
          teamWorkspaceId,
          teamShareGradeId,
        });
        const prev = draftRef.current;
        if (prev.categoryName !== name) return;
        const prevStdScope = standardScopeByIdRef.current;
        const prevPlanScope = planScopeByIdRef.current;
        const deletedStd = new Set(prev.deletedStandardIds);
        const deletedPlan = new Set(prev.deletedPlanIds);
        const userStd = prev.standards.filter((r) => prevStdScope[r.id] === 'user');
        const userPlans = prev.plans.filter((p) => prevPlanScope[p.id] === 'user');
        const userStdIds = new Set(userStd.map((r) => r.id));
        const userPlanIds = new Set(userPlans.map((p) => p.id));
        const standards = [
          ...merged.standards.filter(
            (r) => merged.standardScopeById[r.id] !== 'user' && !userStdIds.has(r.id) && !deletedStd.has(r.id)
          ),
          ...userStd,
        ];
        const plans = [
          ...merged.plans.filter(
            (p) => merged.planScopeById[p.id] !== 'user' && !userPlanIds.has(p.id) && !deletedPlan.has(p.id)
          ),
          ...userPlans,
        ];
        const standardScopeById: Record<string, EvaluationPlanRowScope> = {};
        standards.forEach((r) => {
          standardScopeById[r.id] = userStdIds.has(r.id) ? 'user' : merged.standardScopeById[r.id] ?? 'admin';
        });
        const planScopeById: Record<string, EvaluationPlanRowScope> = {};
        plans.forEach((p) => {
          planScopeById[p.id] = userPlanIds.has(p.id) ? 'user' : merged.planScopeById[p.id] ?? 'admin';
        });
        const next: EvaluationPlanWorkspaceDraft = { ...prev, standards, plans };
        setDraft(next);
        setStandardScopeById(standardScopeById);
        setPlanScopeById(planScopeById);
        setSelectedStandardId((sel) => (sel && standards.some((r) => r.id === sel) ? sel : null));
        saveWorkspaceEvaluationPlanSessionEntry(workspaceId, focusGradeId, name, {
          ...sessionEntry,
          draft: next,
          planScopeById,
          standardScopeById,
        });
      } catch {
        /* 네트워크 실패 시 세션 초안 유지 */
      }
    },
    [workspaceId, focusGradeId, teamWorkspaceId, teamShareGradeId]
  );

  useEffect(() => {
    if (!isActive) return;
    void loadCategories();
    void loadLevelSettings();
  }, [isActive, loadCategories, loadLevelSettings]);

  useEffect(() => {
    let cancelled = false;
    const loadAppliedSelection = async () => {
      if (!workspaceId || !focusGradeId) {
        setAppliedEntries([]);
        setSavedAppliedSnapshots([]);
        return;
      }
      try {
        // 스냅샷 캐시(메모리→LS) 우선. miss일 때만 DB → 캐시 기록(평가셀 가져오기와 공유).
        const snapshots = await resolveWorkspaceEvaluationPlanApplied(workspaceId, focusGradeId);
        if (cancelled) return;
        setSavedAppliedSnapshots(snapshots);
        const fromCacheOrDb: WorkspaceEvaluationPlanAppliedEntry[] = snapshots
          .map((snap) => {
            const planId = (snap.source_plan_id ?? '').trim();
            if (!planId) return null;
            return {
              planId,
              categoryName: snap.source_category_name,
              categoryScope: snap.source_category_scope,
              planScope: normalizePlanScope(snap.source_plan_scope, snap.source_category_scope),
            } satisfies WorkspaceEvaluationPlanAppliedEntry;
          })
          .filter((row): row is WorkspaceEvaluationPlanAppliedEntry => !!row);
        setAppliedEntries(fromCacheOrDb);
        persistAppliedEntries(fromCacheOrDb);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : '적용 평가계획을 불러오지 못했습니다.');
        const fromLs = loadWorkspaceEvaluationPlanAppliedEntries(workspaceId, focusGradeId);
        setAppliedEntries(fromLs);
      }
    };
    void loadAppliedSelection();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, focusGradeId, persistAppliedEntries]);

  useEffect(() => {
    let cancelled = false;
    const resolveAppliedPlans = async () => {
      if (!workspaceId || !focusGradeId || appliedEntries.length === 0) {
        if (!cancelled) {
          setAppliedPlans([]);
          setAppliedStandards([]);
          setAppliedPlanScopeById({});
          setAppliedCategoryByPlanId({});
          liveAppliedBySourceRef.current = new Map();
        }
        return;
      }

      const snapshotBySource = new Map(
        savedAppliedSnapshots
          .filter((snap) => !!snap.source_plan_id)
          .map((snap) => [snap.source_plan_id as string, snap])
      );

      const needLive = appliedEntries.filter((entry) => !snapshotBySource.has(entry.planId));
      const liveBySource = new Map<
        string,
        {
          plan: EvaluationPlanDraft;
          standards: EvaluationStandardDraft[];
          scope: EvaluationPlanRowScope;
          categoryName: string;
        }
      >();

      // 현재 카테고리 draft에 있으면 DB fetch 없이 먼저 채움(적용 직후 네비 네트워크 감소)
      if (categoryName.trim()) {
        for (const entry of needLive) {
          if (entry.categoryName.trim() !== categoryName.trim() || entry.categoryScope !== categoryScope) continue;
          const plan = draft.plans.find((row) => row.id === entry.planId);
          if (!plan) continue;
          const linked = draft.standards.filter((s) => (plan.standard_ids ?? []).includes(s.id));
          liveBySource.set(entry.planId, {
            plan,
            standards: linked,
            scope: normalizePlanScope(planScopeById[plan.id], entry.planScope),
            categoryName: entry.categoryName,
          });
        }
      }

      const stillNeedLive = needLive.filter((entry) => !liveBySource.has(entry.planId));
      const groups = new Map<string, WorkspaceEvaluationPlanAppliedEntry[]>();
      for (const entry of stillNeedLive) {
        const name = entry.categoryName.trim();
        if (!name) continue;
        const key = `${entry.categoryScope}::${name}`;
        const list = groups.get(key) ?? [];
        list.push(entry);
        groups.set(key, list);
      }

      await Promise.all(
        Array.from(groups.entries()).map(async ([key, entries]) => {
          const [scope, ...nameParts] = key.split('::');
          const name = nameParts.join('::');
          const categoryScope =
            scope === 'admin' || scope === 'team' || scope === 'user' ? scope : 'user';
          try {
            const merged = await loadMergedEvaluationPlanCategoryView({
              categoryScope,
              categoryName: name,
              workspaceId,
              gradeId: focusGradeId,
              teamWorkspaceId,
              teamShareGradeId,
            });
            const wanted = new Set(entries.map((e) => e.planId));
            const standardsById = new Map(merged.standards.map((s) => [s.id, s]));
            for (const plan of merged.plans) {
              if (!wanted.has(plan.id)) continue;
              const linked = (plan.standard_ids ?? [])
                .map((id) => standardsById.get(id))
                .filter((row): row is EvaluationStandardDraft => !!row);
              liveBySource.set(plan.id, {
                plan,
                standards: linked,
                scope: merged.planScopeById[plan.id] ?? 'user',
                categoryName: name,
              });
            }
          } catch {
            /* missing live resolved from draft below if possible */
          }
        })
      );

      if (cancelled) return;

      liveAppliedBySourceRef.current = liveBySource;

      const orderedPlans: EvaluationPlanDraft[] = [];
      const standardsById = new Map<string, EvaluationStandardDraft>();
      const scopeById: Record<string, EvaluationPlanRowScope> = {};
      const categoryByPlanId: Record<string, string> = {};
      let entriesNeedScopeSync = false;
      const syncedEntries = appliedEntries.map((entry) => {
        const snap = snapshotBySource.get(entry.planId);
        if (snap) {
          const appliedPlan = { ...snap.plan, id: entry.planId };
          orderedPlans.push(appliedPlan);
          snap.standards.forEach((s) => standardsById.set(s.id, s));
          categoryByPlanId[entry.planId] = snap.source_category_name;

          let displayScope = resolveAppliedPlanScope({
            entryScope: entry.planScope,
            snapScope: snap.source_plan_scope,
            categoryScope: snap.source_category_scope,
          });
          // 미수정 적용본이 잘못 user로 저장된 경우, 원본과 본문이 같으면 출처 색 복구
          const draftOriginal =
            entry.categoryName.trim() === categoryName.trim() && entry.categoryScope === categoryScope
              ? draft.plans.find((p) => p.id === entry.planId)
              : undefined;
          const originalScope = normalizePlanScope(
            draftOriginal ? planScopeById[entry.planId] : undefined,
            displayScope
          );
          if (
            displayScope === 'user' &&
            draftOriginal &&
            originalScope !== 'user' &&
            evaluationPlanTextEqual(appliedPlan, draftOriginal)
          ) {
            displayScope = originalScope;
          }

          scopeById[entry.planId] = displayScope;
          if (entry.planScope !== displayScope) {
            entriesNeedScopeSync = true;
            return { ...entry, planScope: displayScope };
          }
          return entry;
        }
        const live = liveBySource.get(entry.planId);
        if (!live) return entry;
        orderedPlans.push(live.plan);
        live.standards.forEach((s) => standardsById.set(s.id, s));
        const liveScope = live.scope;
        scopeById[entry.planId] = liveScope;
        categoryByPlanId[entry.planId] = live.categoryName || entry.categoryName;
        if (entry.planScope !== liveScope) {
          entriesNeedScopeSync = true;
          return { ...entry, planScope: liveScope };
        }
        return entry;
      });

      setAppliedPlans(orderedPlans);
      setAppliedStandards(Array.from(standardsById.values()));
      setAppliedPlanScopeById(scopeById);
      setAppliedCategoryByPlanId(categoryByPlanId);
      if (entriesNeedScopeSync) {
        setAppliedEntries(syncedEntries);
        persistAppliedEntries(syncedEntries);
      }
    };

    void resolveAppliedPlans();
    return () => {
      cancelled = true;
    };
  }, [
    appliedEntries,
    savedAppliedSnapshots,
    workspaceId,
    focusGradeId,
    teamWorkspaceId,
    teamShareGradeId,
    persistAppliedEntries,
    categoryName,
    categoryScope,
    draft.plans,
    draft.standards,
    planScopeById,
  ]);

  useEffect(() => {
    if (!isActive || !workspaceId || !focusGradeId) return;
    const name = categoryName.trim();
    if (!name) {
      const empty = emptyDraft('');
      setDraft(empty);
      setBaselineJson(normalizeEvaluationPlanWorkspaceDraft(empty));
      setPlanScopeById({});
      setStandardScopeById({});
      return;
    }

    const sessionEntry = readWorkspaceEvaluationPlanSessionEntry(workspaceId, focusGradeId, name);
    if (sessionEntry) {
      if (draft.categoryName !== name) {
        const cloned = cloneWorkspaceEvaluationPlanSessionDraft(sessionEntry);
        setDraft(cloned);
        setBaselineJson(sessionEntry.baselineJson);
        setPlanScopeById(sessionEntry.planScopeById ?? {});
        setStandardScopeById(sessionEntry.standardScopeById ?? {});
        setSelectedStandardId((prev) =>
          prev && cloned.standards.some((row) => row.id === prev) ? prev : null
        );
      }
      if (categoryScope !== 'user') {
        void refreshSharedRowsFromServer(categoryScope, name, sessionEntry);
      }
      return;
    }

    if (draft.categoryName === name) return;

    void loadBundle();
    setFilterQuery('');
    setPlanFilterQuery('');
  }, [categoryScope, categoryName, workspaceId, focusGradeId, isActive, loadBundle, teamWorkspaceId, teamShareGradeId, draft.categoryName, refreshSharedRowsFromServer]);

  useEffect(() => {
    setWorkspaceEvaluationPlanDirty(isDirty);
  }, [isDirty, setWorkspaceEvaluationPlanDirty]);

  const handleSave = useCallback(async () => {
    if (!workspaceId || !focusGradeId) return;
    setSaving(true);
    setError(null);
    const prevSavedForImages = [baselineJsonForImagesRef.current, savedAppliedSnapshotsRef.current];
    try {
      // 저장 중 state가 바뀌어도 최신 draft/적용 선택 기준으로 저장
      let entriesAtSave = appliedEntriesRef.current.slice();
      let appliedPlansAtSave = appliedPlansRef.current.slice();
      const appliedStandardsAtSave = appliedStandardsRef.current.slice();
      let appliedScopeAtSave = { ...appliedPlanScopeByIdRef.current };
      let snapshotsAtSave = savedAppliedSnapshotsRef.current.slice();

      const latestDraft = draftRef.current;
      let draftForSave: EvaluationPlanWorkspaceDraft = {
        ...latestDraft,
        standards: latestDraft.standards.slice(),
        plans: latestDraft.plans.slice(),
        deletedStandardIds: latestDraft.deletedStandardIds.slice(),
        deletedPlanIds: latestDraft.deletedPlanIds.slice(),
      };
      let planScopeForSave: Record<string, EvaluationPlanRowScope> = { ...planScopeByIdRef.current };

      const upsertUserPlanIntoDraft = (plan: EvaluationPlanDraft) => {
        const withCategory = {
          ...plan,
          category_name: plan.category_name || draftForSave.categoryName,
        };
        const exists = draftForSave.plans.some((row) => row.id === withCategory.id);
        draftForSave = {
          ...draftForSave,
          plans: exists
            ? draftForSave.plans.map((row) => (row.id === withCategory.id ? withCategory : row))
            : [...draftForSave.plans, withCategory],
        };
        planScopeForSave = { ...planScopeForSave, [withCategory.id]: 'user' };
      };

      // 적용본이 사용자 출처인데 아직 목록에 사용자 블록이 없으면 분기 추가
      for (const entry of entriesAtSave.slice()) {
        const displayScope = normalizePlanScope(
          appliedScopeAtSave[entry.planId] ?? entry.planScope,
          entry.planScope
        );
        if (displayScope !== 'user') continue;
        const edited =
          appliedPlansAtSave.find((plan) => plan.id === entry.planId) ??
          draftForSave.plans.find((plan) => plan.id === entry.planId) ??
          snapshotsAtSave.find((snap) => snap.source_plan_id === entry.planId)?.plan;
        if (!edited) continue;

        if (planScopeForSave[entry.planId] === 'user') {
          upsertUserPlanIntoDraft({ ...edited, id: entry.planId });
          continue;
        }

        const forked = forkAppliedPlanIntoUserList({
          sourcePlanId: entry.planId,
          editedPlan: { ...edited, id: entry.planId },
          categoryName: entry.categoryName || draftForSave.categoryName,
          draftPlansLength: draftForSave.plans.length,
        });
        upsertUserPlanIntoDraft(forked);
        entriesAtSave = entriesAtSave.map((row) =>
          row.planId === entry.planId ? { ...row, planId: forked.id, planScope: 'user' } : row
        );
        appliedPlansAtSave = appliedPlansAtSave.map((row) => (row.id === entry.planId ? forked : row));
        appliedScopeAtSave = { ...appliedScopeAtSave };
        delete appliedScopeAtSave[entry.planId];
        appliedScopeAtSave[forked.id] = 'user';
        snapshotsAtSave = snapshotsAtSave.map((snap) =>
          snap.source_plan_id === entry.planId
            ? {
                ...snap,
                source_plan_id: forked.id,
                source_plan_scope: 'user',
                plan: { ...forked, id: snap.id },
              }
            : snap
        );
      }

      for (const plan of appliedPlansAtSave) {
        if (normalizePlanScope(appliedScopeAtSave[plan.id] ?? planScopeForSave[plan.id], 'admin') !== 'user') {
          continue;
        }
        upsertUserPlanIntoDraft(plan);
      }

      // 분기된 사용자 평가계획을 화면에 반영
      setDraft(draftForSave);
      draftRef.current = draftForSave;
      setPlanScopeById(planScopeForSave);
      setAppliedEntries(entriesAtSave);
      setAppliedPlans(appliedPlansAtSave);
      setAppliedPlanScopeById(appliedScopeAtSave);
      setSavedAppliedSnapshots(snapshotsAtSave);
      persistAppliedEntries(entriesAtSave);
      planScopeByIdRef.current = planScopeForSave;

      let draftAfterSave = draftForSave;
      let planScopeAfterSave = planScopeForSave;

      if (draftForSave.categoryName.trim()) {
        if (isUserCategory) {
          const saved = await saveWorkspaceEvaluationPlanCategoryBundle(workspaceId, focusGradeId, draftForSave);
          const next: EvaluationPlanWorkspaceDraft = {
            categoryName: saved.categoryName,
            standards: saved.standards,
            plans: saved.plans,
            deletedStandardIds: [],
            deletedPlanIds: [],
          };
          const nextBaseline = normalizeEvaluationPlanWorkspaceDraft(next);
          const scopes: Record<string, EvaluationPlanRowScope> = {};
          saved.plans.forEach((p) => { scopes[p.id] = 'user'; });
          const standardScopes: Record<string, EvaluationPlanRowScope> = {};
          saved.standards.forEach((s) => { standardScopes[s.id] = 'user'; });
          setDraft(next);
          draftRef.current = next;
          setBaselineJson(nextBaseline);
          setPlanScopeById(scopes);
          setStandardScopeById(standardScopes);
          saveWorkspaceEvaluationPlanSessionEntry(workspaceId, focusGradeId, saved.categoryName, {
            draft: next,
            baselineJson: nextBaseline,
            planScopeById: scopes,
            standardScopeById: standardScopes,
          });
          draftAfterSave = next;
          planScopeAfterSave = scopes;
        } else {
          const userPlanIds = getEditablePlanIds(planScopeForSave);
          for (const plan of draftForSave.plans) {
            if (planScopeForSave[plan.id] === 'user') userPlanIds.add(plan.id);
          }
          const userStandardIds = getEditableStandardIds(standardScopeByIdRef.current);
          const savedUserBundle = await saveWorkspaceEvaluationPlanUserPlansOnly(
            workspaceId,
            focusGradeId,
            draftForSave.categoryName,
            draftForSave,
            userPlanIds,
            userStandardIds
          );
          const merged = await loadMergedEvaluationPlanCategoryView({
            categoryScope,
            categoryName: draftForSave.categoryName,
            workspaceId,
            gradeId: focusGradeId,
            teamWorkspaceId,
            teamShareGradeId,
          });
          const mergedPlanIds = new Set(merged.plans.map((p) => p.id));
          const missingUserPlans = savedUserBundle.plans.filter((p) => !mergedPlanIds.has(p.id));
          const plans = missingUserPlans.length ? [...merged.plans, ...missingUserPlans] : merged.plans;
          const planScopes: Record<string, EvaluationPlanRowScope> = { ...merged.planScopeById };
          savedUserBundle.plans.forEach((p) => {
            planScopes[p.id] = 'user';
          });
          const next: EvaluationPlanWorkspaceDraft = {
            categoryName: draftForSave.categoryName,
            standards: merged.standards,
            plans,
            deletedStandardIds: [],
            deletedPlanIds: [],
          };
          const baseline = normalizeEvaluationPlanWorkspaceDraft(
            userEditableSubsetDraft(next, planScopes, merged.standardScopeById)
          );
          setDraft(next);
          draftRef.current = next;
          setBaselineJson(baseline);
          setPlanScopeById(planScopes);
          setStandardScopeById(merged.standardScopeById);
          saveWorkspaceEvaluationPlanSessionEntry(workspaceId, focusGradeId, draftForSave.categoryName, {
            draft: next,
            baselineJson: baseline,
            planScopeById: planScopes,
            standardScopeById: merged.standardScopeById,
          });
          draftAfterSave = next;
          planScopeAfterSave = planScopes;
        }
      }

      // 저장 성공 후: 수정·삭제로 빠진 평가지 이미지 → 서버 참조 확인 후 삭제(적용본 공유 이미지 보호)
      reconcileEvaluationSheetImagesAfterSave(prevSavedForImages, [
        draftAfterSave,
        appliedPlansAtSave,
        snapshotsAtSave,
      ]);

      const snapshotBySource = new Map(
        snapshotsAtSave
          .filter((snap) => !!snap.source_plan_id)
          .map((snap) => [snap.source_plan_id as string, snap])
      );

      // 현재 카테고리 원본을 live ref에 보강
      for (const entry of entriesAtSave) {
        if (entry.categoryName.trim() !== draftAfterSave.categoryName.trim()) continue;
        if (entry.categoryScope !== categoryScope) continue;
        const plan = draftAfterSave.plans.find((row) => row.id === entry.planId);
        if (!plan) continue;
        const linked = draftAfterSave.standards.filter((s) => (plan.standard_ids ?? []).includes(s.id));
        liveAppliedBySourceRef.current.set(entry.planId, {
          plan,
          standards: linked,
          scope: normalizePlanScope(planScopeAfterSave[plan.id], entry.planScope),
          categoryName: entry.categoryName,
        });
      }

      const appliedUpserts = [];
      const skipped: string[] = [];
      for (let idx = 0; idx < entriesAtSave.length; idx += 1) {
        const entry = entriesAtSave[idx];
        const existing = snapshotBySource.get(entry.planId);
        const editedPlan = appliedPlansAtSave.find((plan) => plan.id === entry.planId);
        const live = liveAppliedBySourceRef.current.get(entry.planId);
        const draftOriginal = draftAfterSave.plans.find((p) => p.id === entry.planId);
        const nextPlan = editedPlan ?? live?.plan ?? draftOriginal ?? existing?.plan;
        if (!nextPlan) {
          skipped.push(entry.planId);
          continue;
        }
        const sourcePlanScope = resolveAppliedPlanScope({
          displayScope: appliedScopeAtSave[entry.planId],
          entryScope: entry.planScope,
          liveScope: live?.scope ?? (draftOriginal ? planScopeAfterSave[entry.planId] : undefined),
          snapScope: existing?.source_plan_scope,
          categoryScope: entry.planScope,
        });
        const linkedIds = new Set(nextPlan.standard_ids ?? []);
        const standardsFromLive = live?.standards;
        const standardsFromDraft = draftAfterSave.standards.filter((s) => linkedIds.has(s.id));
        const standardsFromApplied = appliedStandardsAtSave.filter((s) => linkedIds.has(s.id));
        const standards =
          existing && linkedIds.size > 0
            ? [
                ...existing.standards.filter((s) => linkedIds.has(s.id)),
                ...standardsFromApplied.filter((s) => !existing.standards.some((e) => e.id === s.id)),
                ...standardsFromDraft.filter(
                  (s) =>
                    !existing.standards.some((e) => e.id === s.id) &&
                    !standardsFromApplied.some((e) => e.id === s.id)
                ),
              ]
            : standardsFromLive ??
              (standardsFromApplied.length ? standardsFromApplied : standardsFromDraft);

        appliedUpserts.push({
          sourcePlanId: entry.planId,
          sourceCategoryName: existing?.source_category_name || live?.categoryName || entry.categoryName,
          sourceCategoryScope: existing?.source_category_scope || entry.categoryScope,
          sourcePlanScope,
          plan: nextPlan,
          standards,
          sortOrder: idx,
        });
      }

      if (skipped.length > 0) {
        throw new Error(
          `적용 평가계획 ${skipped.length}건의 원본을 찾지 못해 저장할 수 없습니다. 페이지를 새로고침한 뒤 다시 시도해 주세요.`
        );
      }

      const savedApplied = await replaceWorkspaceEvaluationPlanApplied(
        workspaceId,
        focusGradeId,
        appliedUpserts
      );
      setSavedAppliedSnapshots(savedApplied);
      saveWorkspaceEvaluationPlanAppliedSnapshots(workspaceId, focusGradeId, savedApplied);
      const nextEntries: WorkspaceEvaluationPlanAppliedEntry[] = appliedUpserts.map((item) => {
        const snap = savedApplied.find((row) => row.source_plan_id === item.sourcePlanId);
        const fallback = entriesAtSave.find((entry) => entry.planId === item.sourcePlanId);
        return {
          planId: item.sourcePlanId,
          categoryName: snap?.source_category_name || item.sourceCategoryName || fallback?.categoryName || '',
          categoryScope: snap?.source_category_scope || item.sourceCategoryScope || fallback?.categoryScope || 'user',
          planScope: normalizePlanScope(
            snap?.source_plan_scope ?? item.sourcePlanScope,
            fallback?.planScope ?? 'user'
          ),
        };
      });
      setAppliedEntries(nextEntries);
      persistAppliedEntries(nextEntries);

      showSuccess(
        draftRef.current.categoryName.trim()
          ? isUserCategory
            ? '평가계획·적용 평가계획이 저장되었습니다.'
            : '내 성취기준·평가계획·적용 평가계획이 저장되었습니다.'
          : '적용 평가계획이 저장되었습니다.'
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  }, [
    workspaceId,
    focusGradeId,
    isUserCategory,
    categoryScope,
    teamWorkspaceId,
    teamShareGradeId,
    showSuccess,
    persistAppliedEntries,
  ]);

  const handleDiscard = useCallback(() => {
    const baseline = JSON.parse(baselineJson) as EvaluationPlanWorkspaceDraft;
    if (isUserCategory) {
      setDraft(baseline);
      return;
    }
    setDraft((prev) => ({
      categoryName: prev.categoryName,
      standards: [
        ...prev.standards.filter((s) => standardScopeByIdRef.current[s.id] !== 'user'),
        ...baseline.standards,
      ],
      plans: [
        ...prev.plans.filter((p) => planScopeByIdRef.current[p.id] !== 'user'),
        ...baseline.plans,
      ],
      deletedStandardIds: [],
      deletedPlanIds: [],
    }));
  }, [baselineJson, isUserCategory]);

  useEffect(() => {
    saveWorkspaceEvaluationPlanRef.current = { save: handleSave, discard: handleDiscard };
    return () => { saveWorkspaceEvaluationPlanRef.current = null; };
  }, [handleSave, handleDiscard, saveWorkspaceEvaluationPlanRef]);

  const loadDocumentList = useCallback(async (slot: EvaluationPlanAttachmentSlot) => {
    if (!workspaceId || !focusGradeId) return;
    const setLoading = slot === 'criterion' ? setCriterionDocListLoading : setSheetDocListLoading;
    const setError = slot === 'criterion' ? setCriterionDocListError : setSheetDocListError;
    const setItems = slot === 'criterion' ? setCriterionDocListItems : setSheetDocListItems;
    setLoading(true);
    setError(null);
    try {
      const userBundles = await Promise.all(
        userCategoryNames.map((name) => getWorkspaceEvaluationPlanCategoryBundle(workspaceId, focusGradeId, name))
      );
      const plans = userBundles.flatMap((b) => b.plans);
      const standards = userBundles.flatMap((b) => b.standards);
      setItems(
        buildEvaluationPlanDocumentListItems({
          slot,
          plans,
          standards,
          categoryOrder: userCategoryNames,
        })
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : '문서 목록을 불러오지 못했습니다.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, focusGradeId, userCategoryNames]);

  useEffect(() => {
    if (criterionDocListOpen) void loadDocumentList('criterion');
  }, [criterionDocListOpen, loadDocumentList]);

  useEffect(() => {
    if (sheetDocListOpen) void loadDocumentList('sheet');
  }, [sheetDocListOpen, loadDocumentList]);

  useEffect(() => {
    setSelectedPlanIds([]);
  }, [categoryScope, categoryName, focusGradeId]);

  const trySelectCategory = (selection: EvaluationPlanCategorySelection) => {
    if (selection.scope === categoryScope && selection.name === categoryName) return;
    if (isDirty) {
      const ok = window.confirm('저장하지 않은 변경이 있습니다. 카테고리를 변경하시겠습니까?');
      if (!ok) return;
    }
    setCategorySelection(selection);
  };

  const handleCategoryDragEnd = async (event: DragEndEvent) => {
    if (!workspaceId || !focusGradeId || categoryScope !== 'user') return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = userCategoryNames.indexOf(String(active.id));
    const newIndex = userCategoryNames.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(userCategoryNames, oldIndex, newIndex);
    setUserCategoryNames(next);
    setCategoryBusy(true);
    try {
      await reorderWorkspaceEvaluationPlanCategories(workspaceId, focusGradeId, next);
    } catch (e) {
      setError(e instanceof Error ? e.message : '순서 변경에 실패했습니다.');
      void loadCategories();
    } finally {
      setCategoryBusy(false);
    }
  };

  if (!workspaceId) {
    return (
      <section className="cp-admin-notices-page cp-evaluation-plan-page">
        <p className="cp-dashboard-placeholder">워크스페이스를 선택한 뒤 이용해 주세요.</p>
      </section>
    );
  }

  return (
    <section
      className="cp-admin-notices-page cp-admin-notices-page--edu cp-evaluation-plan-page cp-evaluation-plan-page--workspace"
      aria-labelledby="cp-workspace-evaluation-plan-title"
      data-tutorial-ep="page"
    >
      <div className="cp-page-title-row cp-admin-notices-page-title-row cp-evaluation-plan-page-title-row">
        <div className="cp-admin-legal-title-stack">
          <h2 id="cp-workspace-evaluation-plan-title" data-tutorial-ep="title">
            기본 평가계획 등록
          </h2>
        </div>
        <div className="cp-annual-top-actions-save cp-evaluation-plan-page-save cp-page-save-actions--chunky" role="group" aria-label="편집 저장 도구">
          <ProgramToolbarUndoRedoButtons canUndo={false} canRedo={false} onUndo={() => {}} onRedo={() => {}} disabled />
          <button
            type="button"
            className="cp-btn-save-all cp-admin-learning-top-save-btn"
            data-tutorial-ep="save"
            onClick={() => void handleSave()}
            disabled={!isDirty || saving || !categoryName}
            title={isDirty && categoryName ? '저장하기' : '저장할 변경사항 없음'}
          >
            {saving ? '저장 중…' : isDirty && categoryName ? '저장하기' : '변경사항 없음'}
          </button>
        </div>
      </div>

      <div
        className="cp-admin-learning-template-legend cp-evaluation-plan-category-legend"
        role="note"
        data-tutorial-ep="legend"
      >
        <span className="cp-admin-learning-legend-item" data-tutorial-ep="legend-default">
          <span className="cp-admin-learning-template-dot cp-admin-learning-template-dot--default" aria-hidden />
          <span>기본</span>
        </span>
        <span className="cp-admin-learning-legend-item" data-tutorial-ep="legend-team">
          <span className="cp-admin-learning-template-dot cp-admin-learning-template-dot--team" aria-hidden />
          <span>팀장</span>
        </span>
        <span className="cp-admin-learning-legend-item" data-tutorial-ep="legend-user">
          <span className="cp-admin-learning-template-dot cp-admin-learning-template-dot--user" aria-hidden />
          <span>사용자</span>
        </span>
      </div>

      <div className="cp-evaluation-plan-category-tabs-row" data-tutorial-ep="category-row">
        <button
          type="button"
          className="cp-btn-copy-paste cp-admin-learning-add-template-btn cp-evaluation-plan-category-add-btn"
          data-tutorial-ep="add-category"
          onClick={() => {
            setNewCategoryLevelStep(workspaceLevelSettings.defaultLevelStepCount ?? DEFAULT_LEVEL_STEP_COUNT);
            setCategoryModalOpen(true);
          }}
          disabled={categoryBusy}
          aria-label="사용자 카테고리 추가"
        >
          <span className="cp-admin-learning-add-template-label cp-admin-learning-add-template-label--full">사용자 카테고리 추가하기 +</span>
          <span className="cp-admin-learning-add-template-label cp-admin-learning-add-template-label--short">사용자 카테고리 +</span>
        </button>
        <div
          className="cp-admin-legal-subtabs cp-evaluation-plan-category-tabs"
          role="tablist"
          aria-label="카테고리"
          data-tutorial-ep="category-tabs"
        >
          {adminCategoryNames.map((name) => (
            <ReadonlyAdminCategoryTab
              key={`admin-${name}`}
              name={name}
              active={categoryScope === 'admin' && categoryName === name}
              busy={categoryBusy || categoriesLoading}
              onSelect={() => trySelectCategory({ scope: 'admin', name })}
            />
          ))}
          {teamCategoryNames.map((name) => (
            <ReadonlyTeamCategoryTab
              key={`team-${name}`}
              name={name}
              active={categoryScope === 'team' && categoryName === name}
              busy={categoryBusy || categoriesLoading}
              onSelect={() => trySelectCategory({ scope: 'team', name })}
            />
          ))}
          {userCategoryNames.length > 0 ? (
            <DndContext sensors={categoryDndSensors} collisionDetection={closestCenter} onDragEnd={(e) => void handleCategoryDragEnd(e)}>
              <SortableContext items={userCategoryNames} strategy={horizontalListSortingStrategy}>
                {userCategoryNames.map((name) => (
                  <SortableUserCategoryItem
                    key={name}
                    id={name}
                    active={categoryScope === 'user' && categoryName === name}
                    busy={categoryBusy}
                    onSelect={() => trySelectCategory({ scope: 'user', name })}
                    onRename={() => {
                      setRenameSourceName(name);
                      setRenameDraftName(name);
                      setRenameLevelStep(
                        categoryLevelStepByKey[categoryLevelMetaKey('user', name)] ??
                          workspaceLevelSettings.defaultLevelStepCount ??
                          DEFAULT_LEVEL_STEP_COUNT
                      );
                      setRenameModalOpen(true);
                    }}
                    onDelete={() => {
                      setDeleteTargetName(name);
                      setDeleteModalOpen(true);
                    }}
                  />
                ))}
              </SortableContext>
            </DndContext>
          ) : null}
        </div>
        <div className="cp-evaluation-plan-category-doc-list-actions" data-tutorial-ep="doc-actions">
          <button
            type="button"
            className="cp-evaluation-plan-section-head-btn cp-evaluation-plan-section-head-btn--with-icon"
            data-tutorial-ep="level-settings"
            onClick={() => setLevelSettingsModalOpen(true)}
            disabled={categoriesLoading || categoryBusy}
          >
            <BasicSettingIcon />
            성취수준 표시 설정
          </button>
          <button type="button" className="cp-evaluation-plan-section-head-btn cp-evaluation-plan-section-head-btn--with-icon" onClick={() => setCriterionDocListOpen(true)} disabled={categoriesLoading}>
            <ListIcon />
            사용자 기준안 문서 목록
          </button>
          <button type="button" className="cp-evaluation-plan-section-head-btn cp-evaluation-plan-section-head-btn--with-icon" onClick={() => setSheetDocListOpen(true)} disabled={categoriesLoading}>
            <ListIcon />
            평가지 문서 목록
          </button>
        </div>
      </div>

      {error ? <p className="cp-admin-learning-error">{error}</p> : null}

      {categoriesLoading ? (
        <p className="cp-dashboard-placeholder" role="status">카테고리를 불러오는 중…</p>
      ) : !categoryName ? (
        <p className="cp-dashboard-placeholder">카테고리를 선택하거나 추가해 주세요.</p>
      ) : !bundleReady ? (
        <p className="cp-dashboard-placeholder" role="status">데이터를 불러오는 중…</p>
      ) : (
        <div className="cp-admin-notices-grid cp-evaluation-plan-grid cp-evaluation-plan-grid--with-applied">
          <EvaluationStandardListPanel
            standards={draft.standards}
            selectedStandardId={selectedStandardId}
            filterQuery={filterQuery}
            allowBulkEdit={isUserCategory}
            editableStandardIds={isUserCategory ? undefined : editableStandardIds}
            standardScopeById={isUserCategory ? undefined : standardScopeById}
            onFilterQueryChange={setFilterQuery}
            onViewAll={() => setFilterQuery('')}
            onSelectStandard={(id) => setSelectedStandardId((prev) => (prev === id ? null : id))}
            onAddStandard={() => {
              setStandardModalMode('create');
              setStandardModalInitial(createEmptyStandardDraft(categoryName, draft.standards.length));
              setStandardModalOpen(true);
            }}
            onEditStandard={(s) => { setStandardModalMode('edit'); setStandardModalInitial(s); setStandardModalOpen(true); }}
            onDeleteStandard={(id) => {
              setDraft((prev) => ({
                ...prev,
                standards: prev.standards.filter((r) => r.id !== id),
                plans: prev.plans.map((p) => ({ ...p, standard_ids: (p.standard_ids ?? []).filter((sid) => sid !== id) })),
                deletedStandardIds: [...prev.deletedStandardIds, id],
              }));
              if (selectedStandardId === id) setSelectedStandardId(null);
            }}
            onOpenBulkEdit={() => setBulkModalOpen(true)}
          />
          <EvaluationPlanListPanel
            standards={draft.standards}
            plans={visiblePlans}
            allPlans={draft.plans}
            filterQuery={planFilterQuery}
            filterStandardId={selectedStandardId}
            allowAddPlan
            editablePlanIds={isUserCategory ? undefined : editablePlanIds}
            planScopeById={isUserCategory ? undefined : planScopeById}
            levelStepCount={currentLevelStepCount}
            levelDisplayLabels={currentLevelDisplayLabels}
            levelLabelsByStep={workspaceLevelSettings.labelsByStep}
            appliedPlanIds={appliedPlanIds}
            selectedPlanIds={selectedPlanIdSet}
            onToggleSelect={toggleSelectPlan}
            onFilterQueryChange={setPlanFilterQuery}
            onViewAll={() => { setPlanFilterQuery(''); setSelectedStandardId(null); }}
            onEditPlan={(plan) => {
              const editable = isUserCategory || editablePlanIds.has(plan.id);
              setPlanModalTarget('draft');
              setPlanModalMode('edit');
              setPlanModalInitial(cloneEvaluationPlanDraft(plan));
              setPlanModalReadOnly(!editable);
              setPlanModalOpen(true);
            }}
            onDeletePlan={(planId) => {
              setDraft((prev) => ({
                ...prev,
                plans: prev.plans.filter((p) => p.id !== planId),
                deletedPlanIds: [...prev.deletedPlanIds, planId],
              }));
              removeAppliedPlan(planId);
            }}
            onAddPlan={() => {
              setPlanModalTarget('draft');
              setPlanModalMode('create');
              setPlanModalInitial(null);
              setPlanModalReadOnly(false);
              setPlanModalOpen(true);
            }}
          />
          <button
            type="button"
            className="cp-evaluation-plan-apply-arrow"
            data-tutorial-ep="apply-arrow"
            aria-label="선택한 평가계획을 적용 평가계획에 등록"
            title="선택한 평가계획 적용"
            disabled={pendingApplyCount === 0}
            onClick={applySelectedPlans}
          >
            <span className="cp-evaluation-plan-apply-arrow-icon" aria-hidden>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M12 1.875C9.99747 1.875 8.0399 2.46882 6.37486 3.58137C4.70981 4.69392 3.41206 6.27523 2.64572 8.12533C1.87939 9.97543 1.67888 12.0112 2.06955 13.9753C2.46023 15.9393 3.42454 17.7435 4.84055 19.1595C6.25656 20.5755 8.06066 21.5398 10.0247 21.9305C11.9888 22.3211 14.0246 22.1206 15.8747 21.3543C17.7248 20.5879 19.3061 19.2902 20.4186 17.6251C21.5312 15.9601 22.125 14.0025 22.125 12C22.122 9.3156 21.0543 6.74199 19.1562 4.84383C17.258 2.94567 14.6844 1.87798 12 1.875ZM12 19.875C10.4425 19.875 8.91993 19.4131 7.62489 18.5478C6.32985 17.6825 5.32049 16.4526 4.72445 15.0136C4.12841 13.5747 3.97246 11.9913 4.27632 10.4637C4.58018 8.93606 5.3302 7.53287 6.43154 6.43153C7.53288 5.3302 8.93607 4.58017 10.4637 4.27632C11.9913 3.97246 13.5747 4.12841 15.0136 4.72445C16.4526 5.32049 17.6825 6.32985 18.5478 7.62488C19.4131 8.91992 19.875 10.4425 19.875 12C19.8728 14.0879 19.0424 16.0896 17.566 17.566C16.0896 19.0424 14.0879 19.8728 12 19.875ZM16.5459 11.2041C16.6508 11.3086 16.734 11.4328 16.7908 11.5695C16.8476 11.7063 16.8768 11.8529 16.8768 12.0009C16.8768 12.149 16.8476 12.2956 16.7908 12.4324C16.734 12.5691 16.6508 12.6933 16.5459 12.7978L13.5459 15.7978C13.4413 15.9025 13.3171 15.9855 13.1803 16.0421C13.0436 16.0987 12.8971 16.1279 12.7491 16.1279C12.6011 16.1279 12.4545 16.0987 12.3178 16.0421C12.1811 15.9855 12.0568 15.9025 11.9522 15.7978C11.8475 15.6932 11.7645 15.5689 11.7079 15.4322C11.6513 15.2955 11.6221 15.1489 11.6221 15.0009C11.6221 14.8529 11.6513 14.7064 11.7079 14.5697C11.7645 14.4329 11.8475 14.3087 11.9522 14.2041L13.0313 13.125H8.25C7.95164 13.125 7.66549 13.0065 7.45451 12.7955C7.24353 12.5845 7.125 12.2984 7.125 12C7.125 11.7016 7.24353 11.4155 7.45451 11.2045C7.66549 10.9935 7.95164 10.875 8.25 10.875H13.0313L11.9513 9.79594C11.8466 9.69129 11.7636 9.56706 11.707 9.43033C11.6503 9.2936 11.6212 9.14706 11.6212 8.99906C11.6212 8.85107 11.6503 8.70452 11.707 8.5678C11.7636 8.43107 11.8466 8.30683 11.9513 8.20219C12.0559 8.09754 12.1801 8.01453 12.3169 7.9579C12.4536 7.90126 12.6001 7.87211 12.7481 7.87211C12.8961 7.87211 13.0427 7.90126 13.1794 7.9579C13.3161 8.01453 13.4404 8.09754 13.545 8.20219L16.5459 11.2041Z"
                  fill="currentColor"
                />
              </svg>
            </span>
            <span className="cp-evaluation-plan-apply-arrow-text">
              <span>적용</span>
              <span>하기</span>
            </span>
          </button>
          <EvaluationPlanAppliedListPanel
            classLabel={focusClassDisplay.label}
            classGradeNumbers={focusClassDisplay.gradeNumbers}
            standards={appliedStandards}
            plans={appliedPlans}
            planScopeById={appliedPlanScopeById}
            categoryNameByPlanId={appliedCategoryByPlanId}
            levelStepCount={currentLevelStepCount}
            levelDisplayLabels={currentLevelDisplayLabels}
            levelLabelsByStep={workspaceLevelSettings.labelsByStep}
            onEditApplied={(plan) => {
              setPlanModalTarget('applied');
              setPlanModalMode('edit');
              setPlanModalInitial(cloneEvaluationPlanDraft(plan));
              setPlanModalReadOnly(false);
              setPlanModalOpen(true);
            }}
            onDeleteApplied={(planId) => {
              removeAppliedPlan(planId);
            }}
          />
        </div>
      )}

      <EvaluationStandardFormModal
        open={standardModalOpen}
        mode={standardModalMode}
        initial={standardModalInitial}
        categoryName={categoryName}
        levelStepCount={currentLevelStepCount}
        levelDisplayLabels={currentLevelDisplayLabels}
        onClose={() => setStandardModalOpen(false)}
        onSubmit={(standard) => {
          if (standardModalMode === 'create') {
            setStandardScopeById((prev) => {
              const next: Record<string, EvaluationPlanRowScope> = {
                ...prev,
                [standard.id]: 'user' as EvaluationPlanRowScope,
              };
              standardScopeByIdRef.current = next;
              return next;
            });
          }
          commitDraft((prev) => ({
            ...prev,
            standards: standardModalMode === 'create'
              ? [...prev.standards, { ...standard, sort_order: prev.standards.length }]
              : prev.standards.map((r) => (r.id === standard.id ? standard : r)),
          }));
          setStandardModalOpen(false);
          void handleSave();
        }}
      />

      <EvaluationStandardBulkEditModal
        open={bulkModalOpen && isUserCategory}
        categoryName={categoryName}
        standards={draft.standards}
        levelStepCount={currentLevelStepCount}
        levelDisplayLabels={currentLevelDisplayLabels}
        classGradeNumbers={focusClassDisplay.gradeNumbers}
        onClose={() => setBulkModalOpen(false)}
        onApply={(standards) => {
          const incomingIds = new Set(standards.map((r) => r.id));
          const removed = draftRef.current.standards.filter((r) => !incomingIds.has(r.id));
          commitDraft((prev) => ({
            ...prev,
            standards: standards.map((r, idx) => ({ ...r, sort_order: idx, category_name: categoryName })),
            plans: prev.plans.map((p) => ({ ...p, standard_ids: (p.standard_ids ?? []).filter((id) => incomingIds.has(id)) })),
            deletedStandardIds: [...prev.deletedStandardIds, ...removed.map((r) => r.id)],
          }));
          setBulkModalOpen(false);
          void handleSave();
        }}
      />

      <EvaluationPlanFormModal
        open={planModalOpen}
        mode={planModalMode}
        categoryName={
          planModalTarget === 'applied' && planModalInitial
            ? appliedCategoryByPlanId[planModalInitial.id] || categoryName
            : categoryName
        }
        standards={planModalTarget === 'applied' ? appliedStandards : draft.standards}
        initial={planModalInitial}
        initialStandardIds={
          planModalTarget === 'applied'
            ? planModalInitial?.standard_ids ?? []
            : selectedStandardId
              ? [selectedStandardId]
              : []
        }
        sortOrder={planModalTarget === 'applied' ? appliedPlans.length : draft.plans.length}
        defaultLevelStepCount={currentLevelStepCount}
        levelLabelsByStep={workspaceLevelSettings.labelsByStep}
        readOnly={planModalReadOnly}
        workspaceId={workspaceId}
        imageScope="personal"
        aiClassScope={aiClassScope}
        onClose={() => {
          setPlanModalOpen(false);
          setPlanModalTarget('draft');
        }}
        onSubmit={(plan) => {
          if (planModalTarget === 'applied') {
            const live = liveAppliedBySourceRef.current.get(plan.id);
            const draftOriginal = draftRef.current.plans.find((row) => row.id === plan.id);
            const originalPlan = live?.plan ?? draftOriginal;
            const catalogScope = normalizePlanScope(
              live?.scope ?? (draftOriginal ? planScopeByIdRef.current[plan.id] : undefined),
              'admin'
            );
            const originalScope = draftOriginal || live
              ? catalogScope
              : normalizePlanScope(appliedPlanScopeByIdRef.current[plan.id], 'user');
            const previousApplied = appliedPlansRef.current.find((row) => row.id === plan.id);
            const nextScope: EvaluationPlanRowScope = originalPlan
              ? evaluationPlanTextEqual(plan, originalPlan)
                ? originalScope
                : 'user'
              : previousApplied && evaluationPlanTextEqual(plan, previousApplied)
                ? originalScope
                : 'user';

            const sourcePlanId = plan.id;
            const alreadyUserOwned = isCatalogUserOwnedPlan({
              planId: sourcePlanId,
              liveScope: live?.scope,
              planScopeById: planScopeByIdRef.current,
            });
            const needsFork = nextScope === 'user' && !alreadyUserOwned;

            if (needsFork) {
              const forked = forkAppliedPlanIntoUserList({
                sourcePlanId,
                editedPlan: plan,
                categoryName: appliedCategoryByPlanId[sourcePlanId] || categoryName,
                draftPlansLength: draftRef.current.plans.length,
              });
              commitDraft((prev) => ({ ...prev, plans: [...prev.plans, forked] }));
              const nextPlanScopes: Record<string, EvaluationPlanRowScope> = {
                ...planScopeByIdRef.current,
                [forked.id]: 'user',
              };
              planScopeByIdRef.current = nextPlanScopes;
              setPlanScopeById(nextPlanScopes);
              setSelectedPlanIds((prev) => prev.filter((id) => id !== sourcePlanId));
              const nextEntries = appliedEntriesRef.current.map((entry) =>
                entry.planId === sourcePlanId
                  ? { ...entry, planId: forked.id, planScope: 'user' as const }
                  : entry
              );
              appliedEntriesRef.current = nextEntries;
              persistAppliedEntries(nextEntries);
              setAppliedEntries(nextEntries);
              const nextAppliedPlans = appliedPlansRef.current.map((row) =>
                row.id === sourcePlanId ? forked : row
              );
              appliedPlansRef.current = nextAppliedPlans;
              setAppliedPlans(nextAppliedPlans);
              const nextAppliedScope = { ...appliedPlanScopeByIdRef.current };
              delete nextAppliedScope[sourcePlanId];
              nextAppliedScope[forked.id] = 'user';
              appliedPlanScopeByIdRef.current = nextAppliedScope;
              setAppliedPlanScopeById(nextAppliedScope);
              setAppliedCategoryByPlanId((prev) => {
                const cat = prev[sourcePlanId] || categoryName;
                const next = { ...prev };
                delete next[sourcePlanId];
                next[forked.id] = cat;
                return next;
              });
              const nextSnapshots = savedAppliedSnapshotsRef.current.map((snap) =>
                snap.source_plan_id === sourcePlanId
                  ? {
                      ...snap,
                      source_plan_id: forked.id,
                      source_plan_scope: 'user' as const,
                      source_category_scope: snap.source_category_scope,
                      plan: { ...forked, id: snap.id },
                    }
                  : snap
              );
              savedAppliedSnapshotsRef.current = nextSnapshots;
              setSavedAppliedSnapshots(nextSnapshots);
              const prevLive = liveAppliedBySourceRef.current.get(sourcePlanId);
              liveAppliedBySourceRef.current.delete(sourcePlanId);
              liveAppliedBySourceRef.current.set(forked.id, {
                plan: forked,
                standards:
                  prevLive?.standards ??
                  appliedStandardsRef.current.filter((s) => (forked.standard_ids ?? []).includes(s.id)),
                scope: 'user',
                categoryName: appliedCategoryByPlanId[sourcePlanId] || categoryName,
              });
              setPlanModalOpen(false);
              setPlanModalTarget('draft');
              void handleSave();
              return;
            }

            if (alreadyUserOwned || nextScope === 'user') {
              syncUserPlanAcrossListAndApplied(plan);
              if (planScopeByIdRef.current[plan.id] !== 'user') {
                const nextScopes = { ...planScopeByIdRef.current, [plan.id]: 'user' as const };
                planScopeByIdRef.current = nextScopes;
                setPlanScopeById(nextScopes);
              }
              setAppliedPlanDisplayScope(
                plan.id,
                'user',
                setAppliedEntries,
                setAppliedPlanScopeById,
                setSavedAppliedSnapshots,
                persistAppliedEntries
              );
              appliedPlanScopeByIdRef.current = {
                ...appliedPlanScopeByIdRef.current,
                [plan.id]: 'user',
              };
              appliedEntriesRef.current = appliedEntriesRef.current.map((entry) =>
                entry.planId === plan.id ? { ...entry, planScope: 'user' as const } : entry
              );
              savedAppliedSnapshotsRef.current = savedAppliedSnapshotsRef.current.map((snap) =>
                snap.source_plan_id === plan.id ? { ...snap, source_plan_scope: 'user' as const } : snap
              );
            } else {
              const nextAppliedPlans = appliedPlansRef.current.map((row) =>
                row.id === plan.id ? plan : row
              );
              appliedPlansRef.current = nextAppliedPlans;
              setAppliedPlans(nextAppliedPlans);
              if (live) {
                liveAppliedBySourceRef.current.set(plan.id, { ...live, plan });
              }
              const nextSnapshots = savedAppliedSnapshotsRef.current.map((snap) =>
                snap.source_plan_id === plan.id
                  ? {
                      ...snap,
                      plan: { ...plan, id: snap.id },
                      standards: snap.standards,
                      source_plan_scope: nextScope,
                    }
                  : snap
              );
              savedAppliedSnapshotsRef.current = nextSnapshots;
              setSavedAppliedSnapshots(nextSnapshots);
              setAppliedPlanDisplayScope(
                plan.id,
                nextScope,
                setAppliedEntries,
                setAppliedPlanScopeById,
                setSavedAppliedSnapshots,
                persistAppliedEntries
              );
              appliedPlanScopeByIdRef.current = {
                ...appliedPlanScopeByIdRef.current,
                [plan.id]: nextScope,
              };
              appliedEntriesRef.current = appliedEntriesRef.current.map((entry) =>
                entry.planId === plan.id ? { ...entry, planScope: nextScope } : entry
              );
            }
            setPlanModalOpen(false);
            setPlanModalTarget('draft');
            void handleSave();
            return;
          }
          if (planModalMode === 'create') {
            const nextScopes = { ...planScopeByIdRef.current, [plan.id]: 'user' as const };
            planScopeByIdRef.current = nextScopes;
            setPlanScopeById(nextScopes);
            commitDraft((prev) => ({ ...prev, plans: [...prev.plans, plan] }));
          } else {
            commitDraft((prev) => ({
              ...prev,
              plans: prev.plans.map((r) => (r.id === plan.id ? plan : r)),
            }));
            if (appliedPlanIds.has(plan.id) || planScopeByIdRef.current[plan.id] === 'user') {
              syncUserPlanAcrossListAndApplied(plan);
            }
          }
          setPlanModalOpen(false);
          void handleSave();
        }}
        onDraftChange={
          planModalMode === 'edit' && !planModalReadOnly
            ? (plan) => {
                if (planModalTarget === 'applied') {
                  setPlanModalInitial(plan);
                  const live = liveAppliedBySourceRef.current.get(plan.id);
                  const alreadyUserOwned = isCatalogUserOwnedPlan({
                    planId: plan.id,
                    liveScope: live?.scope,
                    planScopeById,
                  });
                  // 기본/팀장 출처는 저장 시 사용자 계획으로 분기. 수정 중 원본 스코프·본문을 덮지 않는다.
                  if (!alreadyUserOwned) return;
                  syncUserPlanAcrossListAndApplied(plan);
                  setAppliedPlanDisplayScope(
                    plan.id,
                    'user',
                    setAppliedEntries,
                    setAppliedPlanScopeById,
                    setSavedAppliedSnapshots,
                    persistAppliedEntries
                  );
                  return;
                }
                commitDraft((prev) => ({
                  ...prev,
                  plans: prev.plans.map((r) => (r.id === plan.id ? plan : r)),
                }));
                setPlanModalInitial(plan);
                if (appliedPlanIds.has(plan.id)) {
                  syncUserPlanAcrossListAndApplied(plan);
                }
              }
            : undefined
        }
      />

      {categoryModalOpen ? (
        <div className="cp-modal-backdrop" role="presentation" onClick={() => setCategoryModalOpen(false)}>
          <div
            className="cp-modal"
            role="dialog"
            aria-modal="true"
            data-tutorial-ep="add-category-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>사용자 카테고리 추가</h3>
            <div className="cp-modal-body">
              <label className="cp-evaluation-plan-field">
                <span>카테고리 이름</span>
                <input type="text" value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} autoFocus />
              </label>
              <label className="cp-evaluation-plan-field">
                <span>성취수준 단계</span>
                <select
                  value={newCategoryLevelStep}
                  onChange={(e) => setNewCategoryLevelStep(clampLevelStepCount(Number(e.target.value)))}
                  aria-label="성취수준 단계"
                >
                  {LEVEL_STEP_COUNT_OPTIONS.map((count) => (
                    <option key={count} value={count}>
                      {formatLevelStepOptionLabel(
                        count,
                        getLevelDisplayLabelsForStep(workspaceLevelSettings.labelsByStep, count)
                      )}
                    </option>
                  ))}
                </select>
              </label>
              <p className="cp-evaluation-plan-level-settings-hint" role="status">
                이 단계는 해당 카테고리에서 평가계획을 만들 때 기본값으로 적용됩니다. 각 평가계획에서 바꿀 수 있습니다.
              </p>
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setCategoryModalOpen(false)}>취소</button>
              <button
                type="button"
                className="cp-btn-primary"
                disabled={categoryBusy}
                onClick={() => {
                  void (async () => {
                    const name = newCategoryName.trim();
                    if (!name || !workspaceId || !focusGradeId) return;
                    setCategoryBusy(true);
                    try {
                      await createWorkspaceEvaluationPlanCategory(
                        workspaceId,
                        focusGradeId,
                        name,
                        newCategoryLevelStep
                      );
                      setCategoryModalOpen(false);
                      setNewCategoryName('');
                      setNewCategoryLevelStep(workspaceLevelSettings.defaultLevelStepCount ?? DEFAULT_LEVEL_STEP_COUNT);
                      await loadCategories();
                      setCategorySelection({ scope: 'user', name });
                    } catch (e) {
                      setError(e instanceof Error ? e.message : '카테고리 추가에 실패했습니다.');
                    } finally {
                      setCategoryBusy(false);
                    }
                  })();
                }}
              >
                추가
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {renameModalOpen ? (
        <div className="cp-modal-backdrop" role="presentation" onClick={() => setRenameModalOpen(false)}>
          <div className="cp-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <h3>카테고리 설정</h3>
            <div className="cp-modal-body">
              <label className="cp-evaluation-plan-field">
                <span>카테고리 이름</span>
                <input type="text" value={renameDraftName} onChange={(e) => setRenameDraftName(e.target.value)} />
              </label>
              <label className="cp-evaluation-plan-field">
                <span>성취수준 단계</span>
                <select
                  value={renameLevelStep}
                  onChange={(e) => setRenameLevelStep(clampLevelStepCount(Number(e.target.value)))}
                  aria-label="성취수준 단계"
                >
                  {LEVEL_STEP_COUNT_OPTIONS.map((count) => (
                    <option key={count} value={count}>
                      {formatLevelStepOptionLabel(
                        count,
                        getLevelDisplayLabelsForStep(workspaceLevelSettings.labelsByStep, count)
                      )}
                    </option>
                  ))}
                </select>
              </label>
              <p className="cp-evaluation-plan-level-settings-hint" role="status">
                이 단계는 해당 카테고리에서 평가계획을 만들 때 기본값으로 적용됩니다. 각 평가계획에서 바꿀 수 있습니다.
              </p>
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setRenameModalOpen(false)}>취소</button>
              <button
                type="button"
                className="cp-btn-primary"
                disabled={categoryBusy}
                onClick={() => {
                  void (async () => {
                    if (!workspaceId || !focusGradeId) return;
                    setCategoryBusy(true);
                    try {
                      const from = renameSourceName.trim();
                      const to = renameDraftName.trim();
                      if (from !== to) await renameWorkspaceEvaluationPlanCategory(workspaceId, focusGradeId, from, to);
                      const fromKey = categoryLevelMetaKey('user', from);
                      const previousLevelStep = categoryLevelStepByKey[fromKey] ?? DEFAULT_LEVEL_STEP_COUNT;
                      const targetName = to || from;
                      if (renameLevelStep !== previousLevelStep) {
                        await updateWorkspaceEvaluationPlanCategoryLevelStep(
                          workspaceId,
                          focusGradeId,
                          targetName,
                          renameLevelStep
                        );
                      }
                      setRenameModalOpen(false);
                      await loadCategories();
                      if (categoryName === from) setCategorySelection({ scope: 'user', name: to || from });
                    } catch (e) {
                      setError(e instanceof Error ? e.message : '설정 변경에 실패했습니다.');
                    } finally {
                      setCategoryBusy(false);
                    }
                  })();
                }}
              >
                저장
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteModalOpen ? (
        <div className="cp-modal-backdrop" role="presentation" onClick={() => setDeleteModalOpen(false)}>
          <div className="cp-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <h3>카테고리 삭제</h3>
            <div className="cp-modal-body">
              <p>「{deleteTargetName}」 카테고리와 포함된 성취기준·평가계획이 삭제됩니다.</p>
            </div>
            <div className="cp-modal-footer">
              <button type="button" className="cp-btn-secondary" onClick={() => setDeleteModalOpen(false)}>취소</button>
              <button
                type="button"
                className="cp-btn-primary"
                disabled={categoryBusy}
                onClick={() => {
                  void (async () => {
                    if (!workspaceId || !focusGradeId) return;
                    setCategoryBusy(true);
                    try {
                      await deleteWorkspaceEvaluationPlanCategory(workspaceId, focusGradeId, deleteTargetName);
                      setDeleteModalOpen(false);
                      await loadCategories();
                    } catch (e) {
                      setError(e instanceof Error ? e.message : '삭제에 실패했습니다.');
                    } finally {
                      setCategoryBusy(false);
                    }
                  })();
                }}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <EvaluationPlanWorkspaceLevelSettingsModal
        open={levelSettingsModalOpen}
        initialSettings={workspaceLevelSettings}
        saving={levelSettingsSaving}
        onClose={() => setLevelSettingsModalOpen(false)}
        onSave={async (settings) => {
          if (!workspaceId || !focusGradeId) return;
          setLevelSettingsSaving(true);
          setError(null);
          try {
            await saveWorkspaceEvaluationPlanLevelSettings(workspaceId, focusGradeId, settings);
            setWorkspaceLevelSettings(settings);
            setLevelSettingsModalOpen(false);
            showSuccess('성취수준 표시 설정이 저장되었습니다.');
          } catch (e) {
            setError(e instanceof Error ? e.message : '성취수준 표시 설정 저장에 실패했습니다.');
          } finally {
            setLevelSettingsSaving(false);
          }
        }}
      />

      <EvaluationPlanDocumentListModal
        open={criterionDocListOpen}
        slot="criterion"
        title="사용자 기준안 문서 목록"
        items={criterionDocListItems}
        isLoading={criterionDocListLoading}
        loadError={criterionDocListError}
        actionsDisabled={saving || categoryBusy}
        onClose={() => setCriterionDocListOpen(false)}
        onDeleteItem={async (listKey) => {
          const item = criterionDocListItems.find((r) => r.listKey === listKey);
          if (!item || !workspaceId) return false;
          await clearWorkspaceEvaluationPlanSlotAttachment({
            workspaceId,
            gradeId: focusGradeId,
            planId: item.planId,
            categoryName: item.categoryName,
            slot: 'criterion',
          });
          await loadDocumentList('criterion');
          return true;
        }}
        onNotify={showSuccess}
      />

      <EvaluationPlanDocumentListModal
        open={sheetDocListOpen}
        slot="sheet"
        items={sheetDocListItems}
        isLoading={sheetDocListLoading}
        loadError={sheetDocListError}
        actionsDisabled={saving || categoryBusy}
        onClose={() => setSheetDocListOpen(false)}
        onDeleteItem={async (listKey) => {
          const item = sheetDocListItems.find((r) => r.listKey === listKey);
          if (!item || !workspaceId) return false;
          await clearWorkspaceEvaluationPlanSlotAttachment({
            workspaceId,
            gradeId: focusGradeId,
            planId: item.planId,
            categoryName: item.categoryName,
            slot: 'sheet',
          });
          await loadDocumentList('sheet');
          return true;
        }}
        onNotify={showSuccess}
      />

      <button
        type="button"
        className="cp-fab-save"
        onClick={() => void handleSave()}
        disabled={!isDirty || saving || !categoryName}
        aria-label="저장하기"
        title={isDirty && categoryName ? '저장하기' : '저장할 변경사항 없음'}
      >
        <img
          src={`${process.env.PUBLIC_URL || ''}/Save.svg`}
          alt=""
          className="cp-fab-save-icon"
        />
      </button>
      <AutoStatusToast state={toastState} />
    </section>
  );
};

export default WorkspaceEvaluationPlanPage;
